import { stripHtml } from './helpers.js';

/**
 * Always-on base fields for session search (title, participants of every
 * displayed role, and sub-session titles). These are searched regardless of
 * embed config.
 */
export const SESSION_BASE_SEARCH_FIELDS = ['title', 'speakers', 'subsessions'];

/**
 * Detail fields for session search — included only when enabled via availableFilters
 * or present in visibleSessionFields.
 */
export const SESSION_DETAIL_SEARCH_FIELDS = [
  'description',
  'location',
  'format',
  'track',
  'tags',
  'level',
  'language',
];

/**
 * Mapping from filter option keys (used in availableFilters / filterOptions) to
 * session field names used in SESSION_DETAIL_SEARCH_FIELDS.
 */
const FILTER_KEY_TO_FIELD = {
  filter_session_by_description: 'description',
  filter_session_by_location: 'location',
  filter_session_by_format: 'format',
  format: 'format',
  filter_session_by_track: 'track',
  track: 'track',
  filter_session_by_tags: 'tags',
  tags: 'tags',
  filter_session_by_level: 'level',
  filter_session_by_language: 'language',
};

/**
 * Derive the session searchable field set from embed config.
 * Starts from SESSION_BASE_SEARCH_FIELDS and adds detail fields when the
 * corresponding filter is enabled (via availableFilters keys) or the field name
 * appears in visibleSessionFields.
 *
 * @param {Object} [availableFilters={}] - from getAvailableFilters(filterOptions, embedFields)
 * @param {string[]} [visibleSessionFields=[]] - getVisibleFieldIdsOfType(groupedFields, SESSIONS)
 * @returns {string[]} deduplicated array of field names, e.g. ['title','speakers','description','tags']
 */
export const buildSessionSearchFields = (availableFilters = {}, visibleSessionFields = []) => {
  const fields = [...SESSION_BASE_SEARCH_FIELDS];

  for (const detailField of SESSION_DETAIL_SEARCH_FIELDS) {
    // Include if a corresponding filter key is truthy in availableFilters
    const enabledViaFilter = Object.entries(FILTER_KEY_TO_FIELD).some(
      ([filterKey, fieldName]) => fieldName === detailField && availableFilters[filterKey]
    );

    // Include if field name appears in visibleSessionFields
    const enabledViaVisibleFields = visibleSessionFields.includes(detailField);

    if (enabledViaFilter || enabledViaVisibleFields) {
      fields.push(detailField);
    }
  }

  return [...new Set(fields)];
};

/**
 * Does one participant match the query?
 *
 * SB-8854: the same field set SB-6071 established for Speakers — name, company
 * name, job title, bio — applies to participants of every role. Role scoping is
 * not done here: the embed payload already carries only the participants whose
 * role the organizer enabled for this embed (SB-8853 filters
 * `session_relationships` by the embed's selected role slugs in SQL, for
 * sessions and sub-sessions alike), so matching every participant present keeps
 * search scope exactly equal to what the embed displays. Bio likewise only
 * matches when the embed exposes the bio field, since `about` is absent from
 * the payload otherwise.
 *
 * @param {Object|null|undefined} participant
 * @param {string} query - Search query, already lowercased by caller
 * @returns {boolean}
 */
export const matchParticipant = (participant, query) => {
  if (!participant) return false;

  if (participant.full_name?.toLowerCase().includes(query)) return true;
  if (participant.title?.toLowerCase().includes(query)) return true;
  if (participant.company_name?.toLowerCase().includes(query)) return true;
  if (stripHtml(participant.about).toLowerCase().includes(query)) return true;

  return false;
};

/**
 * Does a session match the query across the given fields?
 * Handles object/label fields: tags[] -> tag.name, format/track are label strings,
 * description is HTML (stripHtml), speakers[] -> full_name/title/company_name.
 * All field access is ?.-guarded. Returns false (not throw) for falsy session.
 *
 * @param {Object|null|undefined} session - Session object to match against
 * @param {string} query - Search query, already lowercased by caller
 * @param {string[]} fields - Field names to search in
 * @returns {boolean}
 */
export const matchSession = (session, query, fields) => {
  if (!session) return false;

  for (const field of fields) {
    switch (field) {
      case 'title':
        if (session.title?.toLowerCase().includes(query)) return true;
        break;

      case 'description':
        if (stripHtml(session.description).toLowerCase().includes(query)) return true;
        break;

      case 'location':
        if (session.location?.toLowerCase().includes(query)) return true;
        break;

      case 'level':
        if (session.level?.toLowerCase().includes(query)) return true;
        break;

      case 'language':
        if (session.language?.toLowerCase().includes(query)) return true;
        break;

      case 'format':
        if (session.format?.toLowerCase().includes(query)) return true;
        break;

      case 'track':
        if (session.track?.toLowerCase().includes(query)) return true;
        break;

      case 'tags':
        if (session.tags?.some(t => t.name?.toLowerCase().includes(query))) return true;
        break;

      case 'speakers':
        if (session.speakers?.some(s => matchParticipant(s, query))) return true;
        // SB-8854: a sub-session's own participants belong to the parent's
        // searchable content. Returning the parent is what every embed type
        // already does with sub-sessions — nested inline in the schedule
        // itinerary, under the Sub-sessions tab in the session modal — so no
        // new result presentation is introduced.
        if (
          session.subsessions?.some(sub => sub?.speakers?.some(s => matchParticipant(s, query)))
        ) {
          return true;
        }
        break;

      case 'subsessions':
        // SB-8854: sub-session titles were not indexed at all before.
        if (session.subsessions?.some(sub => sub?.title?.toLowerCase().includes(query))) {
          return true;
        }
        break;

      default:
        break;
    }
  }

  return false;
};

/**
 * Does a speaker match the query?
 * Matches speaker entity fields (full_name, title, company_name, about[HTML]) OR
 * any of the speaker's sessions (via matchSession against the full session from sessionsById).
 *
 * @param {Object|null|undefined} speaker - Speaker object to match against
 * @param {string} query - Search query, already lowercased by caller
 * @param {Object} sessionsById - Map of session id -> full session object
 * @param {string[]} sessionFields - Session field set to match the speaker's sessions on
 * @returns {boolean}
 */
export const matchSpeaker = (speaker, query, sessionsById, sessionFields) => {
  if (!speaker) return false;

  // Match speaker entity fields
  if (matchParticipant(speaker, query)) return true;

  // Match via the speaker's sessions by CONTENT only. Exclude the 'speakers'
  // sub-field so searching a speaker name doesn't surface co-presenters who
  // merely share a session — parent or sub-session — since the speaker's own
  // identity is matched above. Sub-session titles are session content, so they
  // do match (SB-8854), like the session description already did.
  if (speaker.sessions) {
    const sessionContentFields = sessionFields.filter(field => field !== 'speakers');
    for (const sessionRef of speaker.sessions) {
      const fullSession = sessionsById?.[sessionRef.id];
      if (matchSession(fullSession, query, sessionContentFields)) return true;
    }
  }

  return false;
};
